import React from 'react';

import { link } from '@aline/routes';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { AlineButton } from 'src/components';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { BoardNameSection } from './settings/BoardNameSection';
import { ColumnsStatusSection } from './settings/ColumnsStatusSection';

const useStyles = makeStyles()(theme => ({
  backButton: {
    padding: theme.spacing(0.5),
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 0,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
  },
  contentInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    margin: '0 auto',
    maxWidth: 664,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 400,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export function BoardSettings(): React.ReactElement | null {
  const { boardId } = useParams<{ boardId: string }>();

  if (!boardId) return null;

  return <BoardSettingsBody boardId={boardId} />;
}

interface BoardSettingsContentProps {
  board: Apollo.GetBoardQuery['board'] | undefined;
  boardId: string;
  loading: boolean;
}

function BoardSettingsContent({ board, boardId, loading }: BoardSettingsContentProps) {
  const { classes } = useStyles();

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!board) {
    return (
      <div className={classes.loading}>
        <Typography variant="body1">Board not found.</Typography>
      </div>
    );
  }

  return (
    <div className={classes.contentInner}>
      <BoardNameSection board={board} />
      <ColumnsStatusSection
        boardId={boardId}
        columns={board.columns ?? []}
        signingColumnId={board.signingColumn?.id ?? null}
      />
    </div>
  );
}

interface BoardSettingsBodyProps {
  boardId: string;
}

function BoardSettingsBody({ boardId }: BoardSettingsBodyProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { data, loading } = Apollo.useGetBoardQuery({
    variables: { id: boardId },
  });

  const board = data?.board;

  return (
    <>
      <Helmet>
        <title>Board Settings</title>
      </Helmet>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.title}>
            <IconButton className={classes.backButton} onClick={() => navigate(-1)}>
              <BackArrowIcon />
            </IconButton>
            <Typography variant="h1">{board ? `${board.name} Settings` : 'Settings'}</Typography>
          </div>
          {board && (
            <AlineButton
              color="primary"
              variant="contained"
              onClick={() =>
                navigate(link.WorkflowBoard(), { state: { selectedBoardId: boardId } })
              }
            >
              Done
            </AlineButton>
          )}
        </div>
        <div className={classes.content}>
          <BoardSettingsContent board={board} boardId={boardId} loading={loading} />
        </div>
      </div>
    </>
  );
}
